import React, { Dispatch, FunctionComponent, useState } from "react";
import CustomError from "./CustomError";

/* Validation */
import { Formik, useFormikContext } from "formik";
import { validationSchema } from "./formConfig";

/* API */
import axios from "axios";
import { Endpoints } from "@internal/common";
import { ClaimReportEnterpriseFormData } from "./ClaimModels";
import FormAlert from "./components/FormAlert";
import { Button, Flex, Text, Title } from "@gjensidige/builders-components";
import FormInput from "./components/FormInput";
import FormTextArea from "./components/FormTextArea";
import FormInputGroup from "./components/FormInputGroup";

export interface IProps {
  claimType: string;
}

interface FormData extends ClaimReportEnterpriseFormData {
  claimType: string;
}

const ClaimReportEnterprise: React.FC<IProps> = (props) => {
  const [formData, setFormData] = useState<FormData>({
    claimType: props.claimType,
    cvrNumber: "",
    policyNumber: "",
    contactPersonName: "",
    email: "",
    contactPersonPhoneNumber: "",
    defendantsAddress: "",
    defendantsEmploymentAndName: "",
    defendantsCVRNumber: "",
    victimsEmploymentAndName: "",
    victimsAddress: "",
    victimsPhoneNumber: "",
    isVictimInEmploymentWithDefendant: "",
    typeofRelationship: "",
    damageDate: "",
    damageLocation: "",
    damageOccuredCircumstances: "",
    extentOfDamage: "",
    claimsMade: "",
    priceEstimateDamage: "",
    wasPropertyForLoanTransportationProcessing: "",
    propertyUsedForDetails: "",
    whoToBlameAndWhy: "",
    policeReportMade: "",
    policeStation: "",
    arbejdstilsynetInvolved: "",
    witnesses: "",
    reportedToOwnInsuranceCompany: "",
    insuranceCompanyName: "",
    victimsPolicyNumber: "",
    hasMotorVehicleKasko: "",
    motorVehicleRegistrationNumber: "",
    consent: false,
  });
  const [submitted, setSubmitted] = useState(false);
  const [apiError, setApiError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const sendFormData = () => {
    setApiError(false);
    setIsLoading(true);
    axios
      .post(Endpoints.ClaimReportDIByggeri, formData)
      .then(() => setSubmitted(true))
      .catch(() => {
        setIsLoading(false);
        setApiError(true);
      });
  };

  return (
    <>
      {submitted ? (
        <FormAlert
          severity="success"
          title="Tak for din anmeldelse"
          text="Vi sender dig en kvitteringsmail med informationerne fra din skadeanmeldelse."
        />
      ) : (
        <>
          <Title size={5}>Anmeldelse af skade på entreprise</Title>
          <Text>* Påkrævede felter</Text>
          <Formik
            enableReinitialize
            initialValues={{
              claimType: formData.claimType,
              cvrNumber: formData.cvrNumber,
              policyNumber: formData.policyNumber,
              contactPersonName: formData.contactPersonName,
              email: formData.email,
              contactPersonPhoneNumber: formData.contactPersonPhoneNumber,
              defendantsAddress: formData.defendantsAddress,
              defendantsEmploymentAndName: formData.defendantsEmploymentAndName,
              defendantsCVRNumber: formData.defendantsCVRNumber,
              victimsEmploymentAndName: formData.victimsEmploymentAndName,
              victimsAddress: formData.victimsAddress,
              victimsPhoneNumber: formData.victimsPhoneNumber,
              isVictimInEmploymentWithDefendant:
                formData.isVictimInEmploymentWithDefendant,
              typeofRelationship: formData.typeofRelationship,
              damageDate: formData.damageDate,
              damageLocation: formData.damageLocation,
              damageOccuredCircumstances: formData.damageOccuredCircumstances,
              extentOfDamage: formData.extentOfDamage,
              claimsMade: formData.claimsMade,
              priceEstimateDamage: formData.priceEstimateDamage,
              wasPropertyForLoanTransportationProcessing:
                formData.wasPropertyForLoanTransportationProcessing,
              propertyUsedForDetails: formData.propertyUsedForDetails,
              whoToBlameAndWhy: formData.whoToBlameAndWhy,
              policeReportMade: formData.policeReportMade,
              policeStation: formData.policeStation,
              arbejdstilsynetInvolved: formData.arbejdstilsynetInvolved,
              witnesses: formData.witnesses,
              reportedToOwnInsuranceCompany:
                formData.reportedToOwnInsuranceCompany,
              insuranceCompanyName: formData.insuranceCompanyName,
              victimsPolicyNumber: formData.victimsPolicyNumber,
              hasMotorVehicleKasko: formData.hasMotorVehicleKasko,
              motorVehicleRegistrationNumber:
                formData.motorVehicleRegistrationNumber,
              consent: formData.consent,
            }}
            validationSchema={validationSchema(formData)}
            onSubmit={() => {
              sendFormData();
            }}
          >
            <RenderForm isLoading={isLoading} setFormData={setFormData} />
          </Formik>
        </>
      )}
      {apiError && (
        <FormAlert
          severity="error"
          text="Der skete en fejl ved indsendelse. Prøv venligst igen senere"
          title="Der skete en fejl."
        />
      )}
    </>
  );
};

interface RenderProps {
  isLoading: boolean;
  setFormData: Dispatch<React.SetStateAction<FormData>>;
}

const RenderForm: FunctionComponent<RenderProps> = ({
  isLoading,
  setFormData,
}) => {
  const {
    handleSubmit,
    handleBlur,
    setFieldValue,
    touched,
    errors,
    values: formData,
  } = useFormikContext<FormData>();

  return (
    <form onSubmit={handleSubmit}>
      <Flex layout={1}>
        <FormInput
          id="cvr-number"
          labelText="CVR-nummer *"
          name="cvrNumber"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("cvrNumber", event.target.value);
            setFormData({
              ...formData,
              cvrNumber: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.cvrNumber && errors.cvrNumber)}
          errorText={errors.cvrNumber}
          value={formData.cvrNumber}
        />
        <FormInput
          id="policy-number"
          labelText="Policenummer *"
          name="policyNumber"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("policyNumber", event.target.value);
            setFormData({
              ...formData,
              policyNumber: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.policyNumber && errors.policyNumber)}
          errorText={errors.policyNumber}
          value={formData.policyNumber}
        />
        <FormInput
          id="contactPersonName"
          labelText="Navn (kontaktperson) *"
          name="contactPersonName"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("contactPersonName", event.target.value);
            setFormData({
              ...formData,
              contactPersonName: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.contactPersonName && errors.contactPersonName)}
          errorText={errors.contactPersonName}
          value={formData.contactPersonName}
        />
        <FormInput
          id="email"
          labelText="Email (kontaktperson) *"
          name="email"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("email", event.target.value);
            setFormData({
              ...formData,
              email: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.email && errors.email)}
          errorText={errors.email}
          value={formData.email}
        />
        <FormInput
          id="contactPersonPhoneNumber"
          labelText="Telefonnummer (kontaktperson) *"
          name="contactPersonPhoneNumber"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("contactPersonPhoneNumber", event.target.value);
            setFormData({
              ...formData,
              contactPersonPhoneNumber: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={
            !!(
              touched.contactPersonPhoneNumber &&
              errors.contactPersonPhoneNumber
            )
          }
          errorText={errors.contactPersonPhoneNumber}
          value={formData.contactPersonPhoneNumber}
        />

        <Title size={5}>Skadevolder</Title>

        <FormTextArea
          id="defendantsAddress"
          labelText="Skadevolders adresse *"
          maxLength={500}
          placeholder="Oplys vejnavn og nummer, by samt postnummer"
          value={formData.defendantsAddress}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
            setFieldValue("defendantsAddress", event.target.value);
            setFormData({
              ...formData,
              defendantsAddress: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.defendantsAddress && errors.defendantsAddress)}
          errorText={errors.defendantsAddress}
        />
        <FormInput
          id="defendantsEmploymentAndName"
          labelText="Skadevolders stilling og navn *"
          name="defendantsEmploymentAndName"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("defendantsEmploymentAndName", event.target.value);
            setFormData({
              ...formData,
              defendantsEmploymentAndName: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={
            !!(
              touched.defendantsEmploymentAndName &&
              errors.defendantsEmploymentAndName
            )
          }
          errorText={errors.defendantsEmploymentAndName}
          value={formData.defendantsEmploymentAndName}
        />
        <FormInput
          id="defendantsCVRNumber"
          labelText="Skadevolders CVR-nummer *"
          name="defendantsCVRNumber"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("defendantsCVRNumber", event.target.value);
            setFormData({
              ...formData,
              defendantsCVRNumber: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={
            !!(touched.defendantsCVRNumber && errors.defendantsCVRNumber)
          }
          errorText={errors.defendantsCVRNumber}
          value={formData.defendantsCVRNumber}
        />

        <Title size={5}>Skadelidte</Title>

        <FormInput
          id="victimsEmploymentAndName"
          labelText="Skadelidtes stilling og navn *"
          name="victimsEmploymentAndName"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("victimsEmploymentAndName", event.target.value);
            setFormData({
              ...formData,
              victimsEmploymentAndName: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={
            !!(
              touched.victimsEmploymentAndName &&
              errors.victimsEmploymentAndName
            )
          }
          errorText={errors.victimsEmploymentAndName}
          value={formData.victimsEmploymentAndName}
        />
        <FormTextArea
          id="victimsAddress"
          labelText="Skadelidtes adresse *"
          maxLength={500}
          placeholder="Oplys vejnavn og nummer, by samt postnummer"
          value={formData.victimsAddress}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
            setFieldValue("victimsAddress", event.target.value);
            setFormData({
              ...formData,
              victimsAddress: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.victimsAddress && errors.victimsAddress)}
          errorText={errors.victimsAddress}
        />
        <FormInput
          id="victimsPhoneNumber"
          labelText="Skadelidtes telefonnummer *"
          name="victimsPhoneNumber"
          helpText="I tidsrummet 8-16"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("victimsPhoneNumber", event.target.value);
            setFormData({
              ...formData,
              victimsPhoneNumber: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.victimsPhoneNumber && errors.victimsPhoneNumber)}
          errorText={errors.victimsPhoneNumber}
          value={formData.victimsPhoneNumber}
        />
        <FormInputGroup
          labelText="Står skadelidte i tjenesteforhold til skadevolder? *"
          id="isVictimInEmploymentWithDefendant"
          onChange={(event) => {
            setFormData({
              ...formData,
              isVictimInEmploymentWithDefendant: event.at(0) ?? "",
            });
            setFieldValue(
              "isVictimInEmploymentWithDefendant",
              event.at(0) ?? "",
            );
          }}
          options={[
            {
              label: "Ja",
              value: "Ja",
            },
            {
              label: "Nej",
              value: "Nej",
            },
          ]}
          type="radio"
          value={formData.isVictimInEmploymentWithDefendant}
          onBlur={handleBlur}
          invalid={
            !!(
              touched.isVictimInEmploymentWithDefendant &&
              errors.isVictimInEmploymentWithDefendant
            )
          }
          errorText={errors.isVictimInEmploymentWithDefendant}
        />
        {formData.isVictimInEmploymentWithDefendant === "Ja" && (
          <FormInputGroup
            labelText="Hvilket forhold? *"
            id="typeofRelationship"
            onChange={(event) => {
              setFormData({
                ...formData,
                typeofRelationship: event.at(0) ?? "",
              });
              setFieldValue("typeofRelationship", event.at(0) ?? "");
            }}
            options={[
              {
                label: "Ansat",
                value: "Ansat",
              },
              {
                label: "Ejer",
                value: "Ejer",
              },
              {
                label: "Andet",
                value: "Andet",
              },
            ]}
            type="radio"
            value={formData.typeofRelationship}
            onBlur={handleBlur}
            invalid={
              !!(touched.typeofRelationship && errors.typeofRelationship)
            }
            errorText={errors.typeofRelationship}
          />
        )}
        <Title size={5}>Skaden</Title>
        <FormInput
          id="damageDate"
          labelText="Hvornår skete skaden? *"
          placeholder="Dato og klokkeslæt/tidsrum"
          name="damageDate"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("damageDate", event.target.value);
            setFormData({
              ...formData,
              damageDate: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.damageDate && errors.damageDate)}
          errorText={errors.damageDate}
          value={formData.damageDate}
        />
        <FormTextArea
          id="damageLocation"
          labelText="Hvor skete skaden? *"
          maxLength={500}
          placeholder="Angiv så nøjagtigt som muligt"
          value={formData.damageLocation}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
            setFieldValue("damageLocation", event.target.value);
            setFormData({
              ...formData,
              damageLocation: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.damageLocation && errors.damageLocation)}
          errorText={errors.damageLocation}
        />
        <FormTextArea
          id="damageOccuredCircumstances"
          labelText="Under hvilke omstændigheder skete skaden? *"
          maxLength={500}
          value={formData.damageOccuredCircumstances}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
            setFieldValue("damageOccuredCircumstances", event.target.value);
            setFormData({
              ...formData,
              damageOccuredCircumstances: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={
            !!(
              touched.damageOccuredCircumstances &&
              errors.damageOccuredCircumstances
            )
          }
          errorText={errors.damageOccuredCircumstances}
        />
        <FormTextArea
          id="extentOfDamage"
          labelText="Beskriv skadens omfang *"
          maxLength={500}
          value={formData.extentOfDamage}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
            setFieldValue("extentOfDamage", event.target.value);
            setFormData({
              ...formData,
              extentOfDamage: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.extentOfDamage && errors.extentOfDamage)}
          errorText={errors.extentOfDamage}
        />
        <FormTextArea
          id="claimsMade"
          labelText="Hvilke erstatningskrav er fremsat? *"
          maxLength={500}
          value={formData.claimsMade}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
            setFieldValue("claimsMade", event.target.value);
            setFormData({
              ...formData,
              claimsMade: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.claimsMade && errors.claimsMade)}
          errorText={errors.claimsMade}
        />
        <FormInput
          id="priceEstimateDamage"
          labelText="Hvilket beløb anslår du skaden til? *"
          name="priceEstimateDamage"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("priceEstimateDamage", event.target.value);
            setFormData({
              ...formData,
              priceEstimateDamage: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={
            !!(touched.priceEstimateDamage && errors.priceEstimateDamage)
          }
          errorText={errors.priceEstimateDamage}
          value={formData.priceEstimateDamage}
        />
        <FormInputGroup
          labelText="Havde forsikrede det beskadigede til lån, afbenyttelse, befordring eller bearbejdelse (reparation)? *"
          id="wasPropertyForLoanTransportationProcessing"
          onChange={(event) => {
            setFormData({
              ...formData,
              wasPropertyForLoanTransportationProcessing: event.at(0) ?? "",
            });
            setFieldValue(
              "wasPropertyForLoanTransportationProcessing",
              event.at(0) ?? "",
            );
          }}
          options={[
            {
              label: "Ja",
              value: "Ja",
            },
            {
              label: "Nej",
              value: "Nej",
            },
          ]}
          type="radio"
          value={formData.wasPropertyForLoanTransportationProcessing}
          onBlur={handleBlur}
          invalid={
            !!(
              touched.wasPropertyForLoanTransportationProcessing &&
              errors.wasPropertyForLoanTransportationProcessing
            )
          }
          errorText={errors.wasPropertyForLoanTransportationProcessing}
        />
        {formData.wasPropertyForLoanTransportationProcessing === "Ja" && (
          <FormInput
            id="propertyUsedForDetails"
            labelText="Hvordan? *"
            helpText="Håndværkerens og dit eget overslag. Husk at angive om prisen er med eller uden moms. Vi beder dig desuden eftersende overslaget samt opgørelse over skaden på info@bygfs.dk"
            name="propertyUsedForDetails"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setFieldValue("propertyUsedForDetails", event.target.value);
              setFormData({
                ...formData,
                propertyUsedForDetails: event.target.value,
              });
            }}
            onBlur={handleBlur}
            invalid={
              !!(
                touched.propertyUsedForDetails && errors.propertyUsedForDetails
              )
            }
            errorText={errors.propertyUsedForDetails}
            value={formData.propertyUsedForDetails}
          />
        )}
        <FormTextArea
          id="whoToBlameAndWhy"
          labelText="Hvem mener du bærer skylden for skaden og hvorfor? *"
          maxLength={500}
          value={formData.whoToBlameAndWhy}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
            setFieldValue("whoToBlameAndWhy", event.target.value);
            setFormData({
              ...formData,
              whoToBlameAndWhy: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.whoToBlameAndWhy && errors.whoToBlameAndWhy)}
          errorText={errors.whoToBlameAndWhy}
        />

        <Title size={5}>Øvrige oplysninger</Title>

        <FormInputGroup
          labelText="Er der optaget politirapport? *"
          id="policeReportMade"
          onChange={(event) => {
            setFormData({
              ...formData,
              policeReportMade: event.at(0) ?? "",
            });
            setFieldValue("policeReportMade", event.at(0) ?? "");
          }}
          options={[
            {
              label: "Ja",
              value: "Ja",
            },
            {
              label: "Nej",
              value: "Nej",
            },
          ]}
          type="radio"
          value={formData.policeReportMade}
          onBlur={handleBlur}
          invalid={!!(touched.policeReportMade && errors.policeReportMade)}
          errorText={errors.policeReportMade}
        />
        {formData.policeReportMade === "Ja" && (
          <FormInput
            id="policeStation"
            labelText="Hvilken politistation? *"
            name="policeStation"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setFieldValue("policeStation", event.target.value);
              setFormData({
                ...formData,
                policeStation: event.target.value,
              });
            }}
            onBlur={handleBlur}
            invalid={!!(touched.policeStation && errors.policeStation)}
            errorText={errors.policeStation}
            value={formData.policeStation}
          />
        )}
        <FormInputGroup
          labelText="Har Arbejdstilsynet været involveret? *"
          id="arbejdstilsynetInvolved"
          onChange={(event) => {
            setFormData({
              ...formData,
              arbejdstilsynetInvolved: event.at(0) ?? "",
            });
            setFieldValue("arbejdstilsynetInvolved", event.at(0) ?? "");
          }}
          options={[
            {
              label: "Ja",
              value: "Ja",
            },
            {
              label: "Nej",
              value: "Nej",
            },
          ]}
          type="radio"
          value={formData.arbejdstilsynetInvolved}
          onBlur={handleBlur}
          invalid={
            !!(
              touched.arbejdstilsynetInvolved && errors.arbejdstilsynetInvolved
            )
          }
          errorText={errors.arbejdstilsynetInvolved}
        />
        <FormTextArea
          id="witnesses"
          labelText="Var der vidner til skadehændelsen? *"
          placeholder="Hvis ja, noter stillinger, navne og kontaktoplysninger"
          maxLength={500}
          value={formData.witnesses}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
            setFieldValue("witnesses", event.target.value);
            setFormData({
              ...formData,
              witnesses: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.witnesses && errors.witnesses)}
          errorText={errors.witnesses}
        />
        <FormInputGroup
          labelText="Har skadelidte anmeldt skaden til eget forsikringsselskab? *"
          id="reportedToOwnInsuranceCompany"
          onChange={(event) => {
            setFormData({
              ...formData,
              reportedToOwnInsuranceCompany: event.at(0) ?? "",
            });
            setFieldValue("reportedToOwnInsuranceCompany", event.at(0) ?? "");
          }}
          options={[
            {
              label: "Ja",
              value: "Ja",
            },
            {
              label: "Nej",
              value: "Nej",
            },
          ]}
          type="radio"
          value={formData.reportedToOwnInsuranceCompany}
          onBlur={handleBlur}
          invalid={
            !!(
              touched.reportedToOwnInsuranceCompany &&
              errors.reportedToOwnInsuranceCompany
            )
          }
          errorText={errors.reportedToOwnInsuranceCompany}
        />
        {formData.reportedToOwnInsuranceCompany === "Ja" && (
          <>
            <FormInput
              id="insuranceCompanyName"
              labelText="I hvilket selskab er skadelidte forsikret? *"
              name="insuranceCompanyName"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setFieldValue("insuranceCompanyName", event.target.value);
                setFormData({
                  ...formData,
                  insuranceCompanyName: event.target.value,
                });
              }}
              onBlur={handleBlur}
              invalid={
                !!(touched.insuranceCompanyName && errors.insuranceCompanyName)
              }
              errorText={errors.insuranceCompanyName}
              value={formData.insuranceCompanyName}
            />
            <FormInput
              id="victimsPolicyNumber"
              labelText="Hvad er skadelidtes policenummer? *"
              name="victimsPolicyNumber"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setFieldValue("victimsPolicyNumber", event.target.value);
                setFormData({
                  ...formData,
                  victimsPolicyNumber: event.target.value,
                });
              }}
              onBlur={handleBlur}
              invalid={
                !!(touched.victimsPolicyNumber && errors.victimsPolicyNumber)
              }
              errorText={errors.victimsPolicyNumber}
              value={formData.victimsPolicyNumber}
            />
          </>
        )}
        <FormInputGroup
          labelText="Hvis skade på køretøj: Er køretøjet forsikret med kaskoforsikring?"
          id="hasMotorVehicleKasko"
          onChange={(event) => {
            setFormData({
              ...formData,
              hasMotorVehicleKasko: event.at(0) ?? "",
            });
            setFieldValue("hasMotorVehicleKasko", event.at(0) ?? "");
          }}
          options={[
            {
              label: "Ja",
              value: "Ja",
            },
            {
              label: "Nej",
              value: "Nej",
            },
            {
              label: "Ved ikke",
              value: "Ved ikke",
            },
          ]}
          type="radio"
          value={formData.hasMotorVehicleKasko}
          onBlur={handleBlur}
          invalid={
            !!(touched.hasMotorVehicleKasko && errors.hasMotorVehicleKasko)
          }
          errorText={errors.hasMotorVehicleKasko}
        />
        <FormInput
          id="motorVehicleRegistrationNumber"
          labelText="Hvis køretøj, noter evt. registreringsnummer på det beskadigede køretøj"
          name="motorVehicleRegistrationNumber"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("motorVehicleRegistrationNumber", event.target.value);
            setFormData({
              ...formData,
              motorVehicleRegistrationNumber: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={
            !!(
              touched.motorVehicleRegistrationNumber &&
              errors.motorVehicleRegistrationNumber
            )
          }
          errorText={errors.motorVehicleRegistrationNumber}
          value={formData.motorVehicleRegistrationNumber}
        />
        <FormInput
          id="consent-id"
          labelText="Jeg erklærer herved, at foranstående oplysninger er i overensstemmelse med de faktiske forhold, og at jeg vil yde selskabet enhver assistance i forbindelse med skaden. *"
          name="consent"
          type="checkbox"
          value="consent"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("consent", event.target.checked);
            setFormData({
              ...formData,
              consent: event.target.checked,
            });
          }}
          onBlur={handleBlur}
          checked={formData.consent}
        />
        {touched.consent && errors.consent && (
          <>
            <br />
            <CustomError errorText={errors.consent} />
          </>
        )}
        <Button loading={isLoading} type="submit" flexible>
          Send
        </Button>
      </Flex>
    </form>
  );
};

export default ClaimReportEnterprise;
